import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowUp, BookOpen, Search, Menu, X, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KnowledgeBasePage() {
  const [data, setData] = useState({ toc: [], sections: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    axios.get(`${API}/knowledge-base/json`).then((r) => {
      setData(r.data || { toc: [], sections: [] });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (anchor) => {
    const el = document.getElementById(anchor);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileTocOpen(false);
  };

  const filteredToc = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.toc;
    return data.toc.filter((t) =>
      t.title.toLowerCase().includes(q) || (t.part || "").toLowerCase().includes(q)
    );
  }, [data.toc, query]);

  // Group toc by part
  const grouped = useMemo(() => {
    const map = new Map();
    filteredToc.forEach((t) => {
      if (!map.has(t.part)) map.set(t.part, []);
      map.get(t.part).push(t);
    });
    return Array.from(map.entries());
  }, [filteredToc]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A]" data-testid="knowledge-base-page">
      {/* Top brand bar */}
      <div className="bg-gradient-to-r from-[#7C3AED] via-[#A78BFA] to-[#EC4899] text-white pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-white/80 mb-1">LocalBest.ai</p>
              <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']" id="toc-top">Knowledge Base · Operator Manual</h1>
              <p className="mt-2 text-white/85 max-w-2xl text-sm md:text-base">
                The unified field manual for clients, team members, and AI assistants — covering listings,
                content, the Trusted Network™, commissions, OnlyBest Creators, and admin operations.
              </p>
            </div>
            <a
              href={`${API}/knowledge-base/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 text-white text-sm font-semibold transition"
              data-testid="kb-download-pdf"
            >
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Sticky TOC — desktop */}
        <aside className="hidden lg:block">
          <div className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto pr-3 border-r border-slate-200">
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter…"
                  className="w-full pl-7 pr-3 py-1.5 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                  data-testid="kb-search"
                />
              </div>
            </div>
            <nav>
              {grouped.map(([part, items]) => (
                <div key={part} className="mb-4">
                  <p className="text-[10px] uppercase tracking-wider text-[#7C3AED] font-bold mb-1.5">{part}</p>
                  <ul className="space-y-0.5">
                    {items.map((t, idx) => (
                      <li key={t.anchor}>
                        <button
                          onClick={() => scrollTo(t.anchor)}
                          className="block w-full text-left text-sm py-1 px-2 rounded hover:bg-slate-100 text-slate-700 hover:text-[#7C3AED] transition"
                          data-testid={`kb-toc-${t.anchor}`}
                        >
                          <span className="text-slate-400 font-mono text-xs mr-1.5">{String(data.toc.indexOf(t) + 1).padStart(2, "0")}</span>
                          {t.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* Mobile TOC toggle */}
        <div className="lg:hidden flex items-center justify-between gap-2 mb-2">
          <button
            onClick={() => setMobileTocOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 text-slate-700 text-sm font-medium"
            data-testid="kb-mobile-toc-toggle"
          >
            {mobileTocOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            Contents
          </button>
          <a
            href={`${API}/knowledge-base/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#7C3AED] text-white text-sm font-semibold"
          >
            <Download className="w-4 h-4" /> PDF
          </a>
        </div>

        {/* Content */}
        <article className="prose max-w-none">
          {/* Mobile TOC overlay */}
          {mobileTocOpen && (
            <div className="lg:hidden mb-4 p-4 rounded-xl bg-white border border-slate-200">
              {grouped.map(([part, items]) => (
                <div key={part} className="mb-3 last:mb-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#7C3AED] font-bold mb-1">{part}</p>
                  <ul>
                    {items.map((t) => (
                      <li key={t.anchor}>
                        <button onClick={() => scrollTo(t.anchor)} className="block w-full text-left text-sm py-1 text-slate-700 hover:text-[#7C3AED]">{t.title}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* TOC card on top of page (also click-targets) */}
          <div id="toc" className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3 text-[#7C3AED]">
              <BookOpen className="w-5 h-5" />
              <h2 className="text-lg font-bold m-0">Table of Contents</h2>
            </div>
            {loading ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                {data.toc.map((t, idx) => (
                  <button
                    key={t.anchor}
                    onClick={() => scrollTo(t.anchor)}
                    className="text-left text-sm py-1.5 text-slate-700 hover:text-[#7C3AED] flex items-center gap-2"
                  >
                    <span className="text-slate-400 font-mono text-xs">{String(idx + 1).padStart(2, "0")}.</span>
                    <span className="flex-1 truncate">{t.title}</span>
                    <span className="text-[10px] text-slate-400">{t.part?.replace("Part ", "")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {data.sections.map((sec, idx) => (
            <motion.section
              key={sec.anchor}
              id={sec.anchor}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-10 scroll-mt-6"
              data-testid={`kb-section-${sec.anchor}`}
            >
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#7C3AED] font-bold mb-1">{sec.part}</p>
              <h2 className="text-2xl font-bold font-['Outfit'] text-slate-900 mt-0 mb-3">
                <span className="text-slate-300 font-mono text-base mr-2">{String(idx + 1).padStart(2, "0")}</span>
                {sec.title}
              </h2>
              <div className="h-[2px] bg-gradient-to-r from-[#7C3AED] to-transparent mb-4" />

              {sec.blocks.map((b, i) => {
                if (b.type === "p") return <p key={i} className="text-slate-700 leading-relaxed">{b.text}</p>;
                if (b.type === "h3") return <h3 key={i} className="text-base font-bold text-slate-900 mt-4 mb-2">{b.text}</h3>;
                if (b.type === "ul") return (
                  <ul key={i} className="list-disc pl-6 my-3 text-slate-700 space-y-1">
                    {b.items.map((li, j) => <li key={j} className="leading-relaxed">{li}</li>)}
                  </ul>
                );
                if (b.type === "callout") return (
                  <div key={i} className="my-4 rounded-lg bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-900">
                    <strong>Tip:</strong> {b.text}
                  </div>
                );
                return null;
              })}

              <div className="mt-6 text-right">
                <button onClick={() => scrollTo("toc")} className="inline-flex items-center gap-1 text-xs text-[#7C3AED] hover:underline">
                  ↑ Back to Table of Contents
                </button>
              </div>
            </motion.section>
          ))}

          <div className="mt-12 mb-4 p-5 rounded-xl bg-slate-900 text-white">
            <p className="text-xs uppercase tracking-wider text-[#A78BFA] font-bold mb-1">Stay current</p>
            <p className="text-sm">
              This guide is auto-generated from the live LocalBest.ai system. The PDF version is identical and always
              reflects the latest features. <a href="https://localbest.ai" className="text-[#00E5FF] inline-flex items-center gap-1">localbest.ai <ExternalLink className="w-3 h-3" /></a>
            </p>
          </div>
          <p className="text-xs text-slate-400 mb-12">© {new Date().getFullYear()} AI Horse-Powered Systems · LocalBest.ai · All rights reserved.</p>
        </article>
      </div>

      {/* Floating back-to-top */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-[#7C3AED] hover:bg-[#8B5CF6] text-white shadow-lg flex items-center justify-center transition"
          data-testid="kb-back-to-top"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
